import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, DollarSign, ShoppingBag, Receipt, Calendar, Plus, Loader2, ArrowUpRight, ArrowDownRight, Filter, X, Trash2 } from 'lucide-react';
import api from '../../api/axios';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { toast } from 'sonner';

const EXPENSE_CATEGORIES = [
  { id: 'stock', label: 'Compra de Stock' },
  { id: 'servicios', label: 'Servicios' },
  { id: 'mantenimiento', label: 'Mantenimiento' },
  { id: 'salarios', label: 'Salarios' },
  { id: 'otros', label: 'Otros' }
];

const PERIODS = [
  { id: 'day', label: 'Hoy' },
  { id: 'week', label: 'Esta Semana' },
  { id: 'month', label: 'Este Mes' },
  { id: 'year', label: 'Este Año' },
  { id: 'custom', label: 'Personalizado' }
];

export const FinanceDashboard = () => {
  const [period, setPeriod] = useState('day');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [showSalesDetail, setShowSalesDetail] = useState(false);
  const [showExpensesDetail, setShowExpensesDetail] = useState(false);
  const [salesList, setSalesList] = useState([]);
  const [expensesList, setExpensesList] = useState([]);

  useEffect(() => {
    fetchSummary();
  }, [period, customStart, customEnd]);

  const fetchSummary = async () => {
    setLoading(true);
    try {
      let url = `/admin/finance/summary?period=${period}`;
      if (period === 'custom' && customStart && customEnd) {
        url += `&start_date=${customStart}&end_date=${customEnd}`;
      }
      const response = await api.get(url);
      setSummary(response.data);
    } catch (error) {
      toast.error('Error al cargar resumen financiero');
    } finally {
      setLoading(false);
    }
  };

  const fetchSales = async () => {
    try {
      let url = `/admin/sales?`;
      if (period === 'custom' && customStart && customEnd) {
        url += `start_date=${customStart}&end_date=${customEnd}`;
      } else if (summary) {
        url += `start_date=${summary.start_date.split('T')[0]}&end_date=${summary.end_date.split('T')[0]}`;
      }
      const response = await api.get(url);
      setSalesList(response.data.sales);
      setShowSalesDetail(true);
    } catch (error) {
      toast.error('Error al cargar ventas');
    }
  };

  const fetchExpenses = async () => {
    try {
      let url = `/admin/expenses?`;
      if (period === 'custom' && customStart && customEnd) {
        url += `start_date=${customStart}&end_date=${customEnd}`;
      } else if (summary) {
        url += `start_date=${summary.start_date.split('T')[0]}&end_date=${summary.end_date.split('T')[0]}`;
      }
      const response = await api.get(url);
      setExpensesList(response.data.expenses);
      setShowExpensesDetail(true);
    } catch (error) {
      toast.error('Error al cargar gastos');
    }
  };

  const formatCurrency = (value) => {
    return `$${(value || 0).toLocaleString('es-AR')}`;
  };

  const getSaleTypeLabel = (type) => {
    const labels = { product: 'Producto', credit: 'Créditos', membership: 'Membresía', other: 'Otro' };
    return labels[type] || type;
  };

  if (loading && !summary) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="finance-dashboard">
      {/* Period Selector */}
      <div className="flex flex-wrap items-center gap-2">
        {PERIODS.map(p => (
          <Button
            key={p.id}
            variant={period === p.id ? 'default' : 'outline'}
            size="sm"
            onClick={() => setPeriod(p.id)}
            className={period === p.id ? 'bg-primary' : ''}
          >
            {p.label}
          </Button>
        ))}
        
        {period === 'custom' && (
          <div className="flex items-center gap-2 ml-2">
            <input
              type="date"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              className="px-2 py-1 bg-white/5 border border-white/10 rounded text-sm"
            />
            <span className="text-muted-foreground">a</span>
            <input
              type="date"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              className="px-2 py-1 bg-white/5 border border-white/10 rounded text-sm"
            />
          </div>
        )}
        
        <Button
          onClick={() => setShowExpenseModal(true)}
          size="sm"
          className="ml-auto bg-red-500/20 hover:bg-red-500/30 text-red-400"
        >
          <Plus className="h-4 w-4 mr-1" />
          Registrar Gasto
        </Button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {/* Total Sales */}
            <Card 
              className="glass-effect border-white/10 cursor-pointer hover:border-green-500/50 transition-colors"
              onClick={fetchSales}
            >
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center justify-between mb-2">
                  <div className="p-2 bg-green-500/20 rounded-lg">
                    <TrendingUp className="h-5 w-5 text-green-400" />
                  </div>
                  <ArrowUpRight className="h-4 w-4 text-green-400" />
                </div>
                <p className="text-2xl sm:text-3xl font-bold text-green-400">
                  {formatCurrency(summary.summary.total_sales)}
                </p>
                <p className="text-sm text-muted-foreground">
                  {summary.summary.sales_count} ventas
                </p>
              </CardContent>
            </Card>

            {/* Total Expenses */}
            <Card 
              className="glass-effect border-white/10 cursor-pointer hover:border-red-500/50 transition-colors"
              onClick={fetchExpenses}
            >
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center justify-between mb-2">
                  <div className="p-2 bg-red-500/20 rounded-lg">
                    <TrendingDown className="h-5 w-5 text-red-400" />
                  </div>
                  <ArrowDownRight className="h-4 w-4 text-red-400" />
                </div>
                <p className="text-2xl sm:text-3xl font-bold text-red-400">
                  {formatCurrency(summary.summary.total_expenses)}
                </p>
                <p className="text-sm text-muted-foreground">
                  {summary.summary.expenses_count} gastos
                </p>
              </CardContent>
            </Card>

            {/* Balance */}
            <Card className={`glass-effect border-white/10 ${summary.summary.balance >= 0 ? 'border-primary/30' : 'border-red-500/30'}`}>
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center justify-between mb-2">
                  <div className={`p-2 rounded-lg ${summary.summary.balance >= 0 ? 'bg-primary/20' : 'bg-red-500/20'}`}>
                    <DollarSign className={`h-5 w-5 ${summary.summary.balance >= 0 ? 'text-primary' : 'text-red-400'}`} />
                  </div>
                </div>
                <p className={`text-2xl sm:text-3xl font-bold ${summary.summary.balance >= 0 ? 'text-primary' : 'text-red-400'}`}>
                  {formatCurrency(summary.summary.balance)}
                </p>
                <p className="text-sm text-muted-foreground">Balance Neto</p>
              </CardContent>
            </Card>
          </div>

          {/* Breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Sales by Type */}
            <Card className="glass-effect border-white/10">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <ShoppingBag className="h-4 w-4" />
                  Ventas por Tipo
                </CardTitle>
              </CardHeader>
              <CardContent>
                {Object.keys(summary.sales_by_type).length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">Sin ventas en este período</p>
                ) : (
                  <div className="space-y-2">
                    {Object.entries(summary.sales_by_type).map(([type, data]) => (
                      <div key={type} className="flex items-center justify-between p-2 bg-white/5 rounded-lg">
                        <div>
                          <p className="font-medium">{getSaleTypeLabel(type)}</p>
                          <p className="text-xs text-muted-foreground">{data.count} ventas</p>
                        </div>
                        <p className="font-bold text-green-400">{formatCurrency(data.total)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Expenses by Category */}
            <Card className="glass-effect border-white/10">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Receipt className="h-4 w-4" />
                  Gastos por Categoría
                </CardTitle>
              </CardHeader>
              <CardContent>
                {Object.keys(summary.expenses_by_category).length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">Sin gastos en este período</p>
                ) : (
                  <div className="space-y-2">
                    {Object.entries(summary.expenses_by_category).map(([cat, data]) => (
                      <div key={cat} className="flex items-center justify-between p-2 bg-white/5 rounded-lg">
                        <div>
                          <p className="font-medium">{EXPENSE_CATEGORIES.find(c => c.id === cat)?.label || cat}</p>
                          <p className="text-xs text-muted-foreground">{data.count} gastos</p>
                        </div>
                        <p className="font-bold text-red-400">{formatCurrency(data.total)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Sales by Payment Method */}
            <Card className="glass-effect border-white/10">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <DollarSign className="h-4 w-4" />
                  Ingresos por Método de Pago
                </CardTitle>
              </CardHeader>
              <CardContent>
                {Object.keys(summary.sales_by_payment).length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">Sin datos</p>
                ) : (
                  <div className="space-y-2">
                    {Object.entries(summary.sales_by_payment).map(([method, total]) => (
                      <div key={method} className="flex items-center justify-between p-2 bg-white/5 rounded-lg">
                        <p className="font-medium capitalize">{method}</p>
                        <p className="font-bold text-primary">{formatCurrency(total)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recent Activity */}
            <Card className="glass-effect border-white/10">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Actividad Reciente
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                  {[...summary.recent_sales.map(s => ({ ...s, isExpense: false })), 
                    ...summary.recent_expenses.map(e => ({ ...e, isExpense: true }))]
                    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                    .slice(0, 6)
                    .map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-white/5 rounded-lg text-sm">
                        <div className="flex items-center gap-2">
                          {item.isExpense ? (
                            <ArrowDownRight className="h-4 w-4 text-red-400" />
                          ) : (
                            <ArrowUpRight className="h-4 w-4 text-green-400" />
                          )}
                          <span className="truncate max-w-[120px]">
                            {item.isExpense ? item.concept : getSaleTypeLabel(item.type)}
                          </span>
                        </div>
                        <span className={item.isExpense ? 'text-red-400' : 'text-green-400'}>
                          {item.isExpense ? '-' : '+'}{formatCurrency(item.isExpense ? item.amount : item.total)}
                        </span>
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {/* Expense Modal */}
      <ExpenseModal 
        isOpen={showExpenseModal} 
        onClose={() => setShowExpenseModal(false)}
        onSuccess={() => { setShowExpenseModal(false); fetchSummary(); }}
      />

      {/* Sales Detail Modal */}
      <DetailModal
        isOpen={showSalesDetail}
        onClose={() => setShowSalesDetail(false)}
        title="Detalle de Ventas"
        items={salesList}
        type="sales"
      />

      {/* Expenses Detail Modal */}
      <DetailModal
        isOpen={showExpensesDetail}
        onClose={() => setShowExpensesDetail(false)}
        title="Detalle de Gastos"
        items={expensesList}
        type="expenses"
        onDelete={async (id) => {
          try {
            await api.delete(`/admin/expenses/${id}`);
            toast.success('Gasto eliminado');
            setExpensesList(expensesList.filter(e => e.id !== id));
            fetchSummary();
          } catch (error) {
            toast.error('Error al eliminar');
          }
        }}
      />
    </div>
  );
};

// Expense Modal Component
const ExpenseModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    category: 'stock',
    concept: '',
    amount: '',
    payment_method: 'efectivo',
    supplier: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/admin/expenses', {
        ...formData,
        amount: parseFloat(formData.amount)
      });
      toast.success('Gasto registrado');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar gasto');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div 
        className="bg-background border border-white/10 rounded-xl w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <h3 className="text-lg font-bold">Registrar Gasto</h3>
          <button onClick={onClose} className="p-1 hover:bg-white/10 rounded">
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Categoría</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm"
              >
                {EXPENSE_CATEGORIES.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Monto *</label>
              <input
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm"
                placeholder="0"
                required
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Concepto *</label>
            <input
              type="text"
              value={formData.concept}
              onChange={(e) => setFormData({ ...formData, concept: e.target.value })}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm"
              placeholder="Descripción del gasto"
              required
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Método de Pago</label>
              <select
                value={formData.payment_method}
                onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm"
              >
                <option value="efectivo">Efectivo</option>
                <option value="transferencia">Transferencia</option>
                <option value="tarjeta">Tarjeta</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Proveedor</label>
              <input
                type="text"
                value={formData.supplier}
                onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm"
                placeholder="Opcional"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Notas</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm"
              rows="2"
              placeholder="Notas adicionales..."
            />
          </div>
          
          <div className="flex gap-3 pt-2">
            <Button type="button" variant="ghost" onClick={onClose} className="flex-1">
              Cancelar
            </Button>
            <Button type="submit" disabled={loading} className="flex-1 bg-red-500 hover:bg-red-600">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Registrar Gasto'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Detail Modal Component
const DetailModal = ({ isOpen, onClose, title, items, type, onDelete }) => {
  if (!isOpen) return null;

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div 
        className="bg-background border border-white/10 rounded-xl w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-white/10 flex items-center justify-between flex-shrink-0">
          <h3 className="text-lg font-bold">{title}</h3>
          <button onClick={onClose} className="p-1 hover:bg-white/10 rounded">
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <div className="overflow-y-auto flex-1 p-4">
          {items.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No hay registros</p>
          ) : (
            <div className="space-y-2">
              {items.map(item => (
                <div key={item.id} className="p-3 bg-white/5 rounded-lg">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      {type === 'sales' ? (
                        <>
                          <p className="font-medium">{item.items?.map(i => i.name).join(', ') || 'Venta'}</p>
                          <p className="text-xs text-muted-foreground">
                            {item.customer_name || 'Cliente anónimo'} • {item.payment_method}
                          </p>
                        </>
                      ) : (
                        <>
                          <p className="font-medium">{item.concept}</p>
                          <p className="text-xs text-muted-foreground">
                            {EXPENSE_CATEGORIES.find(c => c.id === item.category)?.label} • {item.payment_method}
                          </p>
                        </>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">{formatDate(item.created_at)}</p>
                    </div>
                    <div className="text-right flex items-center gap-2">
                      <span className={`font-bold ${type === 'sales' ? 'text-green-400' : 'text-red-400'}`}>
                        ${(type === 'sales' ? item.total : item.amount)?.toLocaleString('es-AR')}
                      </span>
                      {type === 'expenses' && onDelete && (
                        <button
                          onClick={() => onDelete(item.id)}
                          className="p-1 hover:bg-red-500/20 rounded text-red-400"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FinanceDashboard;
